import { useEffect } from 'react';
import clx from 'classnames';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { Route, Switch, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { PageLoader } from 'cat-ecommerce-alloy';
import useRetryAction from '../../../hooks/useRetryAction';
import {
  REACT_ROOT_MAIN,
  SHOPPING_CART_PAGE,
  FOOTER_CHECKOUT_PORTAL_TARGET_ID
} from '../../../constants/targets';
import {
  getCheckoutRequiredFields,
  resetCheckout
} from '../../../store/checkout/actions';
import { ERROR_DOMAIN, ERROR_PATH } from '../../../constants/errorConstants';
import { getDealerInfo } from '../../../store/dealer/actions';
import ExceptionBoundary from '../../common/Exception/ExceptionBoundary';
import ExceptionFeedbackBySelector from '../../common/Exception/ExceptionFeedbackBySelector';

import CheckoutSummaryAndPayment from './CheckoutSummaryAndPayment';
import GstDisclaimer from '@app/components/common/product/GstDisclaimer';
import {
  SHOPPING_CART_ROUTES,
  SUMMARY_AND_PAYMENT_ROUTE_LEGACY
} from '@app/constants/routes';
import ShoppingCartPage from './shopping-cart/Container';
import { getCurrentOrderInfo } from '@app/store/orders/actions';
import { GENERIC_USER_ID, STATUS } from '@app/constants/commonConstants';
import ShoppingCartCriticalErrorMessage from './shopping-cart/ShoppingCartCriticalErrorMessage';
import '@app/special-characters-support.scss';
import checkoutStyles from './checkout-styles.scss';
import { PCC_PS_TITLE_UPDATES } from '@app/constants/featureFlags';

const Checkout = () => {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const {
    includeGST,
    isRequiredFieldsLoadSuccessful,
    isRequiredFieldsLoading,
    showGST
  } = useSelector(s => s.checkout);
  const userId = useSelector(s => s.common.userId);
  const { getDealerInfoLoading, getDealerInfoSuccess } = useSelector(
    s => s.dealer
  );
  const currentOrderStatus = useSelector(s => s.orders.getCurrentOrderStatus);
  const isOrderPriceAvailabilityLoading = useSelector(
    s => s.orders.isOrderPriceAvailabilityLoading
  );
  const getCurrentOrderLoading = currentOrderStatus === STATUS.PENDING;
  const getCurrentOrderSuccess = currentOrderStatus === STATUS.RESOLVED;
  const shoppingCartRoutes = SHOPPING_CART_ROUTES.some(route =>
    location.pathname.includes(route)
  );
  const userIdIsUndefined = typeof userId === 'undefined';
  const isGenericUser = userId === GENERIC_USER_ID;
  const shouldOrderRetry =
    currentOrderStatus === STATUS.REJECTED ||
    (shoppingCartRoutes &&
      !userIdIsUndefined &&
      !isGenericUser &&
      currentOrderStatus === STATUS.IDLE);

  const actionOrder = () =>
    getCurrentOrderInfo({
      isInitialCartCall: !!shoppingCartRoutes,
      errorInfo: { domain: ERROR_DOMAIN.CART, path: ERROR_PATH.MAIN }
    });
  useRetryAction(shouldOrderRetry, actionOrder);
  const shouldRetry = getDealerInfoSuccess === false && !getDealerInfoLoading;

  const action = () =>
    getDealerInfo({
      domain: ERROR_DOMAIN.CHECKOUT,
      path: ERROR_PATH.ROOT
    });

  useRetryAction(shouldRetry, action);

  useEffect(() => {
    dispatch(
      getCheckoutRequiredFields({
        errorInfo: { domain: ERROR_DOMAIN.CHECKOUT, path: ERROR_PATH.MAIN }
      })
    );
    return () => {
      dispatch(resetCheckout());
    };
  }, [dispatch]);

  const target = shoppingCartRoutes ? SHOPPING_CART_PAGE : REACT_ROOT_MAIN;

  const titleUpdatesFeatureFlag = useSelector(
    state => state?.featureFlag?.[PCC_PS_TITLE_UPDATES]
  );

  return createPortal(
    <>
      <div
        data-testid="checkout-container"
        className={clx(
          'w-100',
          {
            'mb-5': !shoppingCartRoutes && !titleUpdatesFeatureFlag,
            'mb-0 mb-md-5': !shoppingCartRoutes && titleUpdatesFeatureFlag
          },
          checkoutStyles['checkout-container']
        )}
      >
        <ShoppingCartCriticalErrorMessage isCheckoutSelector />
        <ExceptionBoundary>
          {(getDealerInfoLoading ||
            (isRequiredFieldsLoading && !shoppingCartRoutes) ||
            ((getCurrentOrderLoading || isOrderPriceAvailabilityLoading) &&
              !shoppingCartRoutes)) && <PageLoader />}
          <ExceptionFeedbackBySelector
            selector={state =>
              state?.errors?.[ERROR_DOMAIN.CHECKOUT]?.[ERROR_PATH.ROOT]
            }
          />
          {(isRequiredFieldsLoadSuccessful || shoppingCartRoutes) &&
            getDealerInfoSuccess &&
            (getCurrentOrderSuccess || shoppingCartRoutes) && (
              <Switch>
                <Route
                  path={SUMMARY_AND_PAYMENT_ROUTE_LEGACY}
                  component={CheckoutSummaryAndPayment}
                />
                <Route
                  path={SHOPPING_CART_ROUTES}
                  component={ShoppingCartPage}
                />
              </Switch>
            )}
          {showGST && !shoppingCartRoutes && (
            <div className="row mt-3">
              <GstDisclaimer
                className="col"
                taxMessage={
                  includeGST
                    ? t('CAT_AUSITEM_GST_TAX')
                    : t('CAT_AUSITEM_EXCLUDES_GST_TAX_NO_ASTERISK')
                }
              />
            </div>
          )}
        </ExceptionBoundary>
      </div>
      <div id={FOOTER_CHECKOUT_PORTAL_TARGET_ID} />
    </>,
    document.getElementById(target)
  );
};

export default Checkout;
